import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import {
  IonIcon,
  IonItem, IonLabel, IonList, IonPage,
  IonToggle,
  isPlatform,
} from '@ionic/react';
import { DniReader } from '@pepper/capacitor-dni-reader';
import classNames from 'classnames';
import { idniecap } from 'idniecap';
import {
  chevronBackOutline,
  chevronForwardOutline,
} from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import ArrowIcon from '@/assets/icons/menu-selector-arrow-right-red.svg';
import BiometryPinModal from '@/components/auth/BiometryPinModal';
import { BiometryPinModalProps } from '@/components/auth/BiometryPinModal/BiometryPinModal';
import { PROFILE, ROUTES_PATH } from '@/constants';
import useModal from '@/hooks/useModal';
import { ClientTaxonomyEvents, ClientTaxonomyProps } from '@/interfaces/AnalyticsInterface/AnalyticsInterface';
import { Menu } from '@/interfaces/ConfigurationInterface/ConfigurationInterface';
import { saveVersionControl } from '@/redux/configOptions/config';
import { browserPermissions, changeNotificationsSwitch, updateNotificationStatus } from '@/redux/notifications/notifications';
import { Analytics } from '@/services/analytics';
import { requestVersionControl } from '@/services/api/version';
import { typeOfBiometryAvailable } from '@/services/biometry';
import { checkNotifications, notificationService } from '@/services/notifications';
import { WebNotificationPermission, WebNotificationStatus } from '@/services/notificationsWeb';
import { storageService } from '@/services/storage';
import { RootState } from '@/store';

import styles from './Configuration.module.scss';
import { VersionControl } from '@/interfaces/VersionControlInterface/VersionControlInterface';

export default function Configuration() {
  const dispatch = useDispatch();
  const notificationSwitchChanged = useSelector((state: RootState) => state.notifications.switchChange);
  const [enabledNotifications, setEnabledNotifications] = useState(notificationService.hasEnabledNotifications());
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [cookieProposalReference, setCookieProposalReference] = useState(storageService.hasAGCode());
  const [superExpressFlow, setExpressFlow] = useState(storageService.hasSuperExpress);
  const [isNFCSupported, setIsNFCSupported] = useState(false);
  const [versionControl, setVersionControl] = useState<VersionControl>();
  const notificationStatus = useSelector((state: RootState) => state.notifications.status);
  const notificationPermissions = useSelector((state: RootState) => state.notifications.permission);
  const { presentModal: presentBiometryPinModal } = useModal(BiometryPinModal);
  const checkIsVersionControl = useSelector(
    (state: RootState) => state.configSlice.versionControl,
  );
  useEffect(() => {
    checkBiometryEnabled();
    if (DniReader && Capacitor.isNativePlatform() && isPlatform('android')) {
      DniReader.isSupported().then((result) => {
        setIsNFCSupported(result.isSupported);
      });
    } else if (isPlatform('ios')) {
      idniecap.isNFCEnable().then((result) => {
        if (result.disponible) {
          setIsNFCSupported(true);
        }
      });
    }
  }, []);

  const checkBiometryEnabled = () => {
    const biometrySaved = storageService.getBiometrySaved();
    if (biometrySaved !== null) {
      setBiometricEnabled(true);
    } else {
      setBiometricEnabled(false);
    }
  };

  const menu: Menu[] = [
    { text: 'Cambiar clave de acceso', icon: chevronForwardOutline },
  ];
  const history = useHistory();

  const handleMenu = (selectedKey: string) => {
    const { NOTIFICATIONS } = ROUTES_PATH;
    const lowerCaseSelectedKey = selectedKey.toLocaleLowerCase();
    const menuOptions: Record<string, string> = {
      notificaciones: NOTIFICATIONS,
      'acceso biometrico': '',
      'cambiar clave de acceso': ROUTES_PATH.LOGIN_CHANGE_PASSWORD,
    };
    const selectedRoute = menuOptions[lowerCaseSelectedKey] || '';

    history.push(selectedRoute);
  };

  const toggleAGCode = (e: { checked: boolean }) => {
    if (e.checked) {
      storageService.setAGCode(true);
      setCookieProposalReference(true);
      Analytics.registerAnalyticsEvent({ [ClientTaxonomyEvents.AG_CODE_ENABLEMENT]: {} });
    } else {
      storageService.setAGCode(false);
      setCookieProposalReference(false);
    }
    Analytics.setAnalyticsUserProperties({
      [ClientTaxonomyProps.AG_CODE_ENABLED]: e.checked.toString(),
    });
  };

  useEffect(() => {
    // Comportamiento nativo para notificaciones
    if (Capacitor.isNativePlatform() && (isPlatform('android') || isPlatform('ios'))) {
      checkNotifications().then((notificationsActive) => {
        setEnabledNotifications(notificationsActive);
      });
    } else if (!Capacitor.isNativePlatform()) {
      // Comportamiento web para navegador en móvil
      if (notificationStatus === WebNotificationStatus.GRANTED
        && notificationPermissions === WebNotificationPermission.GRANTED) {
        setEnabledNotifications(true);
      } else {
        setEnabledNotifications(false);
      }
    }
  }, []);

  const mobileNotificationsChange = (e: { checked: boolean }) => {
    if (e.checked) {
      notificationService.enableNotifications().then((status) => setEnabledNotifications(status));
      Analytics.registerAnalyticsEvent({ [ClientTaxonomyEvents.NOTIFICATION_ENABLEMENT]: {} });
      setEnabledNotifications(true);
    } else {
      notificationService.disableNotifications().then((status) => setEnabledNotifications(status));
      Analytics.registerAnalyticsEvent({ [ClientTaxonomyEvents.NOTIFICATION_DISABLEMENT]: {} });
      setEnabledNotifications(false);
    }
  };

  const toggleSuperExpress = (e: { checked: boolean }) => {
    if (e.checked) {
      storageService.setSuperExpress(true);
      setExpressFlow(true);
      Analytics.registerAnalyticsEvent({ [ClientTaxonomyEvents.SUPER_EXPRESS_ENABLEMENT]: {} });
    } else {
      storageService.setSuperExpress(false);
      setExpressFlow(false);
    }
    Analytics.setAnalyticsUserProperties({
      [ClientTaxonomyProps.SUPER_EXPRESS_ENABLED]: e.checked.toString(),
    });
  };

  const webNotificationsChange = (e: { checked: boolean }) => {
    if (e.checked) {
      if (notificationPermissions === WebNotificationPermission.PROMPT) {
        LocalNotifications.requestPermissions().then((permission) => {
          if (permission.display === 'granted') {
            dispatch(browserPermissions(WebNotificationPermission.GRANTED));
            dispatch(updateNotificationStatus(WebNotificationStatus.GRANTED));
            dispatch(changeNotificationsSwitch(true));
            setEnabledNotifications(true);
          } else if (permission.display === 'denied') {
            dispatch(browserPermissions(WebNotificationPermission.DENIED));
            dispatch(updateNotificationStatus(WebNotificationStatus.DENIED));
            dispatch(changeNotificationsSwitch(false));
            setEnabledNotifications(false);
          }
        });
      } else if (notificationPermissions === WebNotificationPermission.GRANTED) {
        dispatch(updateNotificationStatus(WebNotificationStatus.GRANTED));
        dispatch(changeNotificationsSwitch(true));
        setEnabledNotifications(true);
      }
    } else {
      dispatch(updateNotificationStatus(WebNotificationStatus.DENIED));
      dispatch(changeNotificationsSwitch(false));
      setEnabledNotifications(false);
    }
  };

  const handleBiometryChange = async (checked: boolean) => {
    if (checked) {
      const biometryAvailable = await typeOfBiometryAvailable();
      if (biometryAvailable !== undefined) {
        presentBiometryPinModal<BiometryPinModalProps>({
          onDismiss: () => {
            checkBiometryEnabled();
          },
          onResetPassword: () => {
            history.replace(ROUTES_PATH.LOGIN_CHANGE_PASSWORD);
          },
        });
      }
    } else {
      storageService.clearBiometrySaved();
    }
    // setEnabledNotifications(checked);
  };

  const notificationChange = (e: any) => {
    if ((isPlatform('android') || isPlatform('ios')) && Capacitor.isNativePlatform()) {
      mobileNotificationsChange(e.detail);
    } else if (!Capacitor.isNativePlatform()) {
      webNotificationsChange(e.detail);
    }
  };

  const browserPermissionsDenied = () => !Capacitor.isNativePlatform() && notificationPermissions === WebNotificationPermission.DENIED;

  useEffect(() => {
    // Comportamiento web para navegador en móvil
    if (notificationSwitchChanged !== null) {
      setEnabledNotifications(notificationSwitchChanged);
    }
  }, [notificationSwitchChanged]);

  useEffect(() => {
    // Comportamiento web para navegador en móvil
    if (!Capacitor.isNativePlatform()) {
      if (notificationStatus === WebNotificationStatus.GRANTED && notificationPermissions === WebNotificationPermission.GRANTED) {
        setEnabledNotifications(true);
      } else {
        setEnabledNotifications(false);
      }
    }
  }, [notificationStatus, notificationPermissions]);

  useEffect(() => {
    if (Capacitor.isNativePlatform() && !versionControl) {
      if (!checkIsVersionControl) {
        requestVersionControl().then((res) => {
          const versionRes = res as VersionControl;
          setVersionControl(versionRes);
          dispatch(saveVersionControl(versionRes));
        });
      } else {
        setVersionControl(checkIsVersionControl);
      }
    }
  }, []);
  return (
    <IonPage className={styles.configurationPageWrapper}>
      <div className={styles.pageConfiguration}>
        <div className={styles.pageConfigurationTitle}>
          <IonIcon
            md={chevronBackOutline}
            className={styles.pageConfigurationTitleIcon}
            onClick={() => {
              history.push(PROFILE);
            }}
          />
          <IonLabel className={styles.pageConfigurationTitleInfo}>
            Configuración
          </IonLabel>
        </div>
        <div className={styles.pageConfigurationContent}>
          <div className={styles.pageConfigurationContentTitle}>
            <IonLabel className={styles.principalTitle}>Tu App, tus reglas</IonLabel>
            <IonLabel className={styles.principalSubtitle}>Configura la App a tu medida.</IonLabel>
          </div>

          <IonList className={styles.listConfiguration}>
            {/* Comportamiento nativo para notificaciones, se muestra en móvil, no en web */}
            {(isPlatform('android') || isPlatform('ios')) && (
              <>
                <IonItem
                  key="notifications"
                  className={styles.listItems}
                >
                  <div className={classNames(
                    styles.listItemsDataWidth,
                    { [styles.listItemsDeniedPermissions]: browserPermissionsDenied() },
                  )}
                  >
                    <div className={classNames(
                      styles.listItemsData,
                      { [styles.listItemsDataNotificationDenied]: browserPermissionsDenied() },
                    )}
                    >
                      <IonLabel className={styles.listItemsText}>Notificaciones</IonLabel>
                      <IonToggle
                        mode="ios"
                        aria-label="notifications"
                        value="notifications"
                        onIonChange={(ev) => {
                          notificationChange(ev);
                        }}
                        checked={enabledNotifications}
                        disabled={notificationPermissions === WebNotificationPermission.DENIED}
                      />
                    </div>
                    {browserPermissionsDenied()
                      && <span className={styles.permissionsDisabledMsg}>Para recibir notificaciones debes activar los permisos de tu navegador.</span>}
                  </div>
                </IonItem>
                {(Capacitor.isNativePlatform()) && (
                  <IonItem
                    key="biometry"
                    className={styles.listItems}
                  >
                    <div className={styles.listItemsData}>
                      <IonLabel className={styles.listItemsText}>Acceso Biométrico</IonLabel>
                      <IonToggle
                        mode="ios"
                        aria-label="biometry"
                        value="biometry"
                        onIonChange={(ev) => {
                          setBiometricEnabled(ev.detail.checked);
                          handleBiometryChange(ev.detail.checked);
                        }}
                        checked={biometricEnabled}
                      />
                    </div>
                  </IonItem>
                )}
              </>
            )}

            <IonItem
              key="proposal-reference"
              className={styles.listItems}
            // onClick={() => handleMenu(text)}
            >
              <div className={styles.listItemsData}>
                <IonLabel className={styles.listItemsText}>Código AG</IonLabel>
                <IonToggle
                  mode="ios"
                  aria-label="proposal-reference"
                  value="proposal-reference"
                  checked={cookieProposalReference}
                  onIonChange={(e) => toggleAGCode(e.detail)}
                />
              </div>
            </IonItem>
            {isNFCSupported && (
              <IonItem
                key="super-express"
                className={styles.listItems}
              >
                <div className={styles.listItemsData}>
                  <IonLabel className={styles.listItemsText}>Super Express</IonLabel>
                  <IonToggle
                    mode="ios"
                    aria-label="super-express"
                    value="super-express"
                    checked={superExpressFlow ?? versionControl?.super_express_default}
                    onIonChange={(e) => toggleSuperExpress(e.detail)}
                  />
                </div>
              </IonItem>
            )}
            {menu.map(({ text }: Menu) => (
              <IonItem
                key={text}
                className={styles.listItems}
                onClick={() => handleMenu(text)}
              >
                <div className={styles.listItemsData}>
                  <IonLabel className={styles.listItemsText}>{text}</IonLabel>
                  <IonIcon src={ArrowIcon} className={styles.listItemsIcon} />
                </div>
              </IonItem>
            ))}
          </IonList>
        </div>
      </div>
    </IonPage>
  );
}
